import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Target, Hexagon, TrendingUp, Lightbulb, Users, ArrowLeft, Download, Share2, RefreshCw, Loader2, Brain, Lock, ArrowRight, Sparkles } from "lucide-react";
import VerifyEmailModal from "@/components/VerifyEmailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalysisResultsPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showPdfUpgrade, setShowPdfUpgrade] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [planBilling, setPlanBilling] = useState("monthly");
  const [planCurrency, setPlanCurrency] = useState("USD");
  const canDownloadPdf = ["growth", "agency", "enterprise"].includes(user?.plan || "trial");
  const isTrial = !user?.plan || user?.plan === "trial" || user?.plan === "free";
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [iterations, setIterations] = useState([]);
  const [iterationsLoading, setIterationsLoading] = useState(false);

  const upgradePlans = [
    { name: "Starter", key: "starter", monthlyUSD: 39, yearlyUSD: 374, monthlyMXN: 699, yearlyMXN: 6710, analyses: "5 / month", features: ["Full Neuro Score", "Attention Heatmap", "AI Recommendations"], icon: "🎯" },
    { name: "Growth", key: "growth", monthlyUSD: 89, yearlyUSD: 854, monthlyMXN: 1595, yearlyMXN: 15312, analyses: "20 / month", features: ["Everything in Starter", "PDF Reports", "Shareable Links"], icon: "🚀", popular: true },
    { name: "Agency", key: "agency", monthlyUSD: 219, yearlyUSD: 2102, monthlyMXN: 3925, yearlyMXN: 37680, analyses: "60 / month", features: ["Everything in Growth", "Team Collaboration", "Priority Support"], icon: "⚡" },
  ];
  const getUpgradePrice = (p) => {
    if (planCurrency === "MXN") return planBilling === "yearly" ? p.yearlyMXN : p.monthlyMXN;
    return planBilling === "yearly" ? p.yearlyUSD : p.monthlyUSD;
  };
  const [verifyModal, setVerifyModal] = useState(false);

  const handleUpgradePlan = async (planKey) => {
    setCheckoutLoading(planKey);
    try {
      const { data } = await axios.post(`${API}/stripe/checkout`, {
        plan: planKey, billing: planBilling, currency: planCurrency.toLowerCase()
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail || "Something went wrong";
      if (err.response?.status === 403 && detail.toLowerCase().includes("verify")) {
        setVerifyModal(true);
      } else {
        alert(detail);
      }
      setCheckoutLoading(null);
    }
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/analyses/${id}`, { withCredentials: true });
        setAnalysis(data);
        if (data.status === "processing") {
          const interval = setInterval(async () => {
            const { data: d } = await axios.get(`${API}/analyses/${id}`, { withCredentials: true });
            setAnalysis(d);
            if (d.status !== "processing") clearInterval(interval);
          }, 3000);
          return () => clearInterval(interval);
        }
      } catch { navigate("/analyses"); }
      setLoading(false);
    };
    fetch();
  }, [id, navigate]);

  useEffect(() => { if (analysis) setLoading(false); }, [analysis]);

  // Fetch iterations when analysis is loaded and completed
  useEffect(() => {
    if (!analysis || analysis.status !== "completed") return;
    const fetchIterations = async () => {
      setIterationsLoading(true);
      try {
        const { data } = await axios.get(`${API}/analyses/${id}/iterations`, { withCredentials: true });
        setIterations(data.iterations || []);
      } catch (err) {
        console.error("Failed to fetch iterations", err);
      }
      setIterationsLoading(false);
    };
    fetchIterations();
  }, [analysis, id]);

  const handleAnalyzeOptimized = () => {
    // Always use the CURRENT analysis as parent — so the AI gets the most recent context
    navigate(`/analyze/new?parent=${id}`);
  };

  if (loading) return <LoadingSkeleton />;
  if (!analysis) return null;
  if (analysis.status === "processing") return <ProcessingState />;

  const handleReanalyze = async () => {
    try {
      setReanalyzing(true);
      await axios.post(`${API}/analyses/${id}/reanalyze`, {}, { withCredentials: true });
      setAnalysis(prev => ({ ...prev, status: "processing", results: null }));
      const interval = setInterval(async () => {
        const { data: d } = await axios.get(`${API}/analyses/${id}`, { withCredentials: true });
        setAnalysis(d);
        if (d.status !== "processing") {
          clearInterval(interval);
          setReanalyzing(false);
        }
      }, 3000);
    } catch {
      setReanalyzing(false);
    }
  };
  const handleDownloadPDF = async () => {
    if (!canDownloadPdf) {
      setShowPdfUpgrade(true);
      return;
    }
    try {
      const response = await axios.get(`${API}/analyses/${id}/report/pdf`, {
        withCredentials: true,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `AdBit_Report_${analysis.ad_name || "report"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 403) setShowPdfUpgrade(true);
      else console.error("PDF download failed:", err);
    }
  };

  const handleShare = async () => {
    try {
      setSharing(true);
      const { data } = await axios.post(`${API}/analyses/${id}/share`, {}, { withCredentials: true });
      const link = `${window.location.origin}/shared/${data.share_id}`;
      setShareLink(link);
      await navigator.clipboard.writeText(link);
    } catch (err) {
      console.error("Share failed:", err);
    } finally {
      setSharing(false);
    }
  };

  if (analysis.status === "failed") return <FailedState error={analysis.error} onBack={() => navigate("/analyses")} />;

  const r = analysis.results;
  if (!r) return <FailedState error="No results available" onBack={() => navigate("/analyses")} />;

  const scoreColor = r.neuro_score >= 80 ? "text-emerald-500" : r.neuro_score >= 50 ? "text-amber-500" : "text-red-500";
  const ringColor = r.neuro_score >= 80 ? "#10B981" : r.neuro_score >= 50 ? "#F59E0B" : "#EF4444";

  return (
    <div data-testid="analysis-results-page" className="max-w-6xl space-y-6">
      {/* Back */}
      <button onClick={() => navigate("/analyses")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-orange-600 transition-colors">
        <ArrowLeft size={14} /> Back to analyses
      </button>

      {/* Top section */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Ad thumbnail */}
          {analysis.image_base64 && (
            <div className="w-full md:w-48 flex-shrink-0">
              <img src={analysis.image_base64} alt={analysis.ad_name} className="w-full rounded-xl object-cover border border-slate-200" />
            </div>
          )}

          {/* Metadata + Score */}
          <div className="flex-1 flex flex-col md:flex-row justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>{analysis.ad_name}</h1>
                {analysis.parent_analysis_id && analysis.iteration_number && (
                  <Badge className="bg-orange-100 text-orange-700 border-0 text-xs">
                    Iteration v{analysis.iteration_number}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant="secondary" className="text-xs">{analysis.category}</Badge>
                <Badge variant="secondary" className="text-xs">{analysis.platform}</Badge>
                <Badge variant="secondary" className="text-xs">{analysis.objective}</Badge>
              </div>
              <p className="text-xs text-slate-400 mt-2">Analyzed {new Date(analysis.created_at).toLocaleString()}</p>

              <div className="flex gap-2 mt-4 flex-wrap">
                <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={handleDownloadPDF}>
                  {canDownloadPdf ? <Download size={12} className="mr-1" /> : <Lock size={12} className="mr-1" />} PDF
                </Button>
                <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={handleShare} disabled={sharing}>
                  <Share2 size={12} className="mr-1" /> {shareLink ? "Link Copied!" : sharing ? "Generating..." : "Share"}
                </Button>
                <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={handleReanalyze} disabled={reanalyzing}>
                  {reanalyzing ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />}
                  {reanalyzing ? "Analyzing..." : "Re-analyze"}
                </Button>
                <Button size="sm" className="rounded-lg text-xs text-white border-0"
                  style={{ background: "linear-gradient(135deg, #F97316 0%, #D946EF 100%)" }}
                  onClick={handleAnalyzeOptimized}>
                  <Sparkles size={12} className="mr-1" /> Analyze Optimized Version
                </Button>
              </div>
            </div>

            {/* Neuro Score */}
            <div data-testid="neuro-score-display" className="text-center flex-shrink-0">
              <div className="relative w-32 h-32 mx-auto">
                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                  <circle cx="60" cy="60" r="52" fill="none" stroke={ringColor} strokeWidth="8"
                    strokeDasharray={`${(r.neuro_score / 100) * 326.7} 326.7`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-3xl font-bold ${scoreColor}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{r.neuro_score}</span>
                  <span className="text-[10px] text-slate-400">/ 100</span>
                </div>
              </div>
              <p className="text-sm font-bold mt-1" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
                Ad Neuro Score
              </p>
              <Badge className={`mt-1 ${r.neuro_score >= 80 ? "bg-emerald-100 text-emerald-700" : r.neuro_score >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"} border-0`}>
                {r.score_label}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Iterations Comparison (only show if 2+ iterations exist) */}
      {iterations.length >= 2 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
                Version History
              </h2>
              <p className="text-xs text-slate-500 mt-1">Track your progress across iterations</p>
            </div>
            <Badge className="bg-slate-100 text-slate-700 border-0 text-xs">
              {iterations.length} versions
            </Badge>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Version</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500">Neuro Score</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 hidden md:table-cell">Attention</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 hidden md:table-cell">Clarity</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 hidden md:table-cell">Emotion</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 hidden md:table-cell">Brand</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 hidden md:table-cell">Memory</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 hidden md:table-cell">CTA</th>
                  <th className="text-right py-2 px-2 text-xs font-semibold text-slate-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {iterations.map((it, idx) => {
                  const itResults = it.results || {};
                  const itBreakdown = itResults.neuro_breakdown || {};
                  const itScore = itResults.neuro_score || 0;
                  const prevScore = idx > 0 ? (iterations[idx-1].results?.neuro_score || 0) : null;
                  const scoreDelta = prevScore !== null ? itScore - prevScore : null;
                  const isCurrent = it.id === id;
                  const getDimScore = (dim) => itBreakdown[dim]?.score || 0;
                  const getDimDelta = (dim) => {
                    if (idx === 0) return null;
                    const prev = iterations[idx-1].results?.neuro_breakdown?.[dim]?.score || 0;
                    return getDimScore(dim) - prev;
                  };
                  const renderScoreCell = (score, delta) => {
                    const color = score >= 80 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-red-600";
                    return (
                      <div className="flex items-center justify-center gap-1">
                        <span className={`font-semibold ${color}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{score}</span>
                        {delta !== null && delta !== 0 && (
                          <span className={`text-[10px] font-medium ${delta > 0 ? "text-emerald-500" : "text-red-500"}`}>
                            {delta > 0 ? "↑" : "↓"}{Math.abs(delta)}
                          </span>
                        )}
                      </div>
                    );
                  };
                  return (
                    <tr key={it.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${isCurrent ? "bg-orange-50" : ""}`}
                      onClick={() => { if (it.id !== id) navigate(`/analyze/${it.id}`); }}>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`${isCurrent ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-700"} border-0 text-xs`}>
                            v{idx + 1}
                          </Badge>
                          <span className="text-xs text-slate-700 truncate max-w-[150px]">{it.ad_name}</span>
                          {isCurrent && <span className="text-[10px] text-orange-600 font-semibold">CURRENT</span>}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {renderScoreCell(itScore, scoreDelta)}
                      </td>
                      <td className="py-3 px-2 text-center hidden md:table-cell">
                        {renderScoreCell(getDimScore("attention"), getDimDelta("attention"))}
                      </td>
                      <td className="py-3 px-2 text-center hidden md:table-cell">
                        {renderScoreCell(getDimScore("cognitive_clarity"), getDimDelta("cognitive_clarity"))}
                      </td>
                      <td className="py-3 px-2 text-center hidden md:table-cell">
                        {renderScoreCell(getDimScore("emotional_connection"), getDimDelta("emotional_connection"))}
                      </td>
                      <td className="py-3 px-2 text-center hidden md:table-cell">
                        {renderScoreCell(getDimScore("brand_visibility"), getDimDelta("brand_visibility"))}
                      </td>
                      <td className="py-3 px-2 text-center hidden md:table-cell">
                        {renderScoreCell(getDimScore("memorability"), getDimDelta("memorability"))}
                      </td>
                      <td className="py-3 px-2 text-center hidden md:table-cell">
                        {renderScoreCell(getDimScore("cta_effectiveness"), getDimDelta("cta_effectiveness"))}
                      </td>
                      <td className="py-3 px-2 text-right text-xs text-slate-500">
                        {new Date(it.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {iterations.length >= 2 && iterations[iterations.length - 1].results?.neuro_score && iterations[0].results?.neuro_score && (
            <div className="mt-4 p-3 rounded-xl" style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FDF4FF 100%)" }}>
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-600">
                  <span className="font-semibold">Progress:</span> v1 → v{iterations.length}
                </div>
                <div className="text-sm font-bold" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  {(() => {
                    const first = iterations[0].results?.neuro_score || 0;
                    const last = iterations[iterations.length - 1].results?.neuro_score || 0;
                    const delta = last - first;
                    return (
                      <span className={delta >= 0 ? "text-emerald-600" : "text-red-600"}>
                        {first} → {last} ({delta >= 0 ? "+" : ""}{delta} points)
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="attention" className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl w-full flex flex-wrap h-auto" data-testid="result-tabs">
          {[
            { v: "attention", icon: Eye, l: "Attention" },
            { v: "brand", icon: Target, l: "Brand" },
            { v: "neuro", icon: Hexagon, l: "Neuro Score" },
            { v: "engagement", icon: TrendingUp, l: "Engagement" },
            { v: "memory", icon: Brain, l: "Memory" },
            { v: "recommendations", icon: Lightbulb, l: "Recommendations" },
            { v: "audience", icon: Users, l: "Audience" },
          ].map(({ v, icon: I, l }) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-lg py-2 px-3 text-xs font-medium flex items-center gap-1.5 data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
              <I size={13} /> {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-4">
          <TabsContent value="attention">
            {isTrial ? <BlurredTab onUpgrade={() => setShowPlansModal(true)} label="Attention Heatmap"><AttentionTab data={r.attention} image={analysis.image_base64} /></BlurredTab> : <AttentionTab data={r.attention} image={analysis.image_base64} />}
          </TabsContent>
          <TabsContent value="brand">
            {isTrial ? <BlurredTab onUpgrade={() => setShowPlansModal(true)} label="Brand Attention"><BrandTab data={r.brand_attention} /></BlurredTab> : <BrandTab data={r.brand_attention} />}
          </TabsContent>
          <TabsContent value="neuro"><NeuroScoreTab data={r.neuro_breakdown} isTrial={isTrial} onUpgrade={() => setShowPlansModal(true)} /></TabsContent>
          <TabsContent value="engagement">
            {isTrial ? <BlurredTab onUpgrade={() => setShowPlansModal(true)} label="Engagement Predictions"><EngagementTab data={r.engagement} platform={analysis.platform} /></BlurredTab> : <EngagementTab data={r.engagement} platform={analysis.platform} />}
          </TabsContent>
          <TabsContent value="memory">
            {isTrial ? <BlurredTab onUpgrade={() => setShowPlansModal(true)} label="Memory Retention Curve"><MemoryRetentionTab data={r.memory_retention} memorability={r.neuro_breakdown?.memorability} /></BlurredTab> : <MemoryRetentionTab data={r.memory_retention} memorability={r.neuro_breakdown?.memorability} />}
          </TabsContent>
          <TabsContent value="recommendations"><RecommendationsTab data={r.recommendations} isTrial={isTrial} onUpgrade={() => setShowPlansModal(true)} analysisId={id} userPlan={user?.plan || "trial"} originalImage={analysis.image_base64} optimizedImage={analysis.optimized_image} /></TabsContent>
          <TabsContent value="audience">
            {isTrial ? <BlurredTab onUpgrade={() => setShowPlansModal(true)} label="Audience Segmentation"><AudienceTab data={r.audience} /></BlurredTab> : <AudienceTab data={r.audience} />}
          </TabsContent>
        </div>
      </Tabs>

      {/* Upgrade Plans Modal */}
      {(showPdfUpgrade || showPlansModal) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowPdfUpgrade(false); setShowPlansModal(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {showPdfUpgrade ? "Upgrade to Download PDF Reports" : "Upgrade to Unlock Full Report"}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {showPdfUpgrade ? "PDF reports available from the Growth plan" : "Get full access to all analysis insights"}
                </p>
              </div>
              <button onClick={() => { setShowPdfUpgrade(false); setShowPlansModal(false); }} className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200">✕</button>
            </div>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center bg-slate-100 rounded-full p-0.5">
                <button onClick={() => setPlanBilling("monthly")} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${planBilling === "monthly" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>Monthly</button>
                <button onClick={() => setPlanBilling("yearly")} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${planBilling === "yearly" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>Yearly</button>
              </div>
              <div className="flex items-center bg-slate-100 rounded-full p-0.5">
                <button onClick={() => setPlanCurrency("USD")} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${planCurrency === "USD" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>$ USD</button>
                <button onClick={() => setPlanCurrency("MXN")} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${planCurrency === "MXN" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>$ MXN</button>
              </div>
            </div>
            <div className="space-y-3">
              {upgradePlans.map(p => {
                const price = getUpgradePrice(p);
                return (
                  <button key={p.key} onClick={() => handleUpgradePlan(p.key)} disabled={checkoutLoading === p.key}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${p.popular ? "border-orange-400 bg-orange-50/50 hover:border-orange-500" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{p.icon}</span>
                        <span className="text-sm font-bold text-slate-800">{p.name}</span>
                        {p.popular && <span className="text-[9px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">Most Popular</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {checkoutLoading === p.key ? <Loader2 size={16} className="animate-spin text-orange-500" /> : (
                          <>
                            <span className="text-base font-bold text-slate-800" style={{ fontFamily: "JetBrains Mono" }}>${price.toLocaleString()}</span>
                            <span className="text-[10px] text-slate-400">{planCurrency}/{planBilling === "yearly" ? "yr" : "mo"}</span>
                            <ArrowRight size={14} className="text-slate-400" />
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-7">
                      <span className="text-xs text-slate-500 font-medium">{p.analyses}</span>
                      {p.features.map((f, i) => <span key={i} className="text-xs text-slate-400">· {f}</span>)}
                    </div>
                    {planBilling === "yearly" && <p className="text-[10px] text-emerald-500 font-semibold mt-1.5 ml-7">Save with annual billing</p>}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 text-center mt-4">Cancel anytime · No hidden fees</p>
          </div>
        </div>
      )}

      {verifyModal && <VerifyEmailModal onClose={() => setVerifyModal(false)} />}
    </div>
  );
}

/* ═══ BLURRED TAB (Trial plan — shows real content blurred) ═══ */
function BlurredTab({ children, onUpgrade, label }) {
  return (
    <div className="relative">
      <div className="filter blur-[6px] select-none pointer-events-none overflow-hidden max-h-[500px]">
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/40 backdrop-blur-[2px] rounded-2xl">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 text-center max-w-sm mx-4">
          <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mx-auto mb-3">
            <Lock size={20} className="text-orange-500" />
          </div>
          <h3 className="text-base font-bold text-slate-800 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Upgrade to view {label}</h3>
          <p className="text-xs text-slate-500 mb-4">Unlock the full analysis with detailed insights and actionable data.</p>
          <Button onClick={onUpgrade} className="gradient-btn rounded-xl h-9 px-5 text-xs font-semibold">
            Upgrade Plan <ArrowRight size={12} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ═══ ATTENTION TAB ═══ */
function AttentionTab({ data, image }) {
  const [showHeatmap, setShowHeatmap] = useState(true);
  if (!data) return <EmptyTab />;

  const posMap = { "top-left": "20% 20%", "top-center": "50% 15%", "top-right": "80% 20%", "upper-center": "50% 30%", center: "50% 50%", "center-left": "25% 50%", "center-right": "75% 50%", "lower-center": "50% 70%", "bottom-left": "20% 80%", "bottom-center": "50% 85%", "bottom-right": "80% 80%", bottom: "50% 85%", top: "50% 15%", various: "50% 50%" };
  const heatmapGradient = (data.zones || []).map(z => {
    const pos = posMap[z.position] || "50% 50%";
    const pct = z.attention_pct || 10;
    const color = pct > 25 ? "rgba(239,68,68," : pct > 15 ? "rgba(245,158,11," : "rgba(16,185,129,";
    return `radial-gradient(ellipse ${Math.max(60, pct * 4)}px ${Math.max(50, pct * 3)}px at ${pos}, ${color}${Math.min(0.6, pct / 50)}) 0%, transparent 70%)`;
  }).join(",");

  return (
    <div data-testid="attention-tab" className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Heatmap Viewer</h3>
          <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => setShowHeatmap(!showHeatmap)} data-testid="toggle-heatmap">
            {showHeatmap ? "Hide" : "Show"} Heatmap
          </Button>
        </div>
        <div className="relative max-w-lg mx-auto rounded-xl overflow-hidden border border-slate-200">
          {image && <img src={image} alt="Ad" className="w-full object-contain" />}
          {showHeatmap && <div className="absolute inset-0" style={{ background: heatmapGradient || "none" }} />}
        </div>
        <div className="flex items-center justify-center gap-4 mt-3">
          <LegendDot color="bg-red-500" label="High" />
          <LegendDot color="bg-amber-500" label="Medium" />
          <LegendDot color="bg-emerald-500" label="Low" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <MetricCard title="Key Attention Zones">
          {(data.zones || []).map((z, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-orange-500 w-5" style={{ fontFamily: "JetBrains Mono" }}>{i + 1}</span>
                <span className="text-sm text-slate-700">{z.name}</span>
              </div>
              <span className="text-sm font-bold" style={{ fontFamily: "JetBrains Mono", color: z.attention_pct > 25 ? "#EF4444" : z.attention_pct > 15 ? "#F59E0B" : "#10B981" }}>{z.attention_pct}%</span>
            </div>
          ))}
        </MetricCard>
        <MetricCard title="Attention Metrics">
          <MetricRow label="Time to First Fixation" value={`${data.time_to_first_fixation_ms || "—"}ms`} />
          <MetricRow label="Predicted Scan Path" value={(data.scan_path || []).join(" → ")} />
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-2">Distribution</p>
            {Object.entries(data.distribution || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] text-slate-400 w-16 capitalize">{k}</span>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-orange-500 rounded-full" style={{ width: `${v}%` }} /></div>
                <span className="text-[10px] font-medium text-slate-600 w-8 text-right" style={{ fontFamily: "JetBrains Mono" }}>{v}%</span>
              </div>
            ))}
          </div>
        </MetricCard>
      </div>
    </div>
  );
}

/* ═══ BRAND TAB ═══ */
function BrandTab({ data }) {
  if (!data) return <EmptyTab />;
  return (
    <div data-testid="brand-tab" className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
          <p className="text-xs text-slate-500 mb-2">Brand Visibility Score</p>
          <ScoreRing score={data.score || 0} size={100} />
        </div>
        <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <h4 className="text-sm font-bold text-slate-700 mb-3">Brand Elements Detected</h4>
          {(data.elements || []).map((el, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <div><span className="text-sm font-medium text-slate-700">{el.type}</span><span className="text-xs text-slate-400 ml-2">{el.location}</span></div>
              <span className="text-sm font-bold text-orange-600" style={{ fontFamily: "JetBrains Mono" }}>{el.attention_pct}%</span>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <MetricCard title="Brand vs Distractor">
          <div className="flex gap-3 mt-2">
            <div className="flex-1 text-center p-3 bg-orange-50 rounded-xl">
              <p className="text-2xl font-bold text-orange-600" style={{ fontFamily: "JetBrains Mono" }}>{data.brand_vs_distractor?.brand_elements_pct || 0}%</p>
              <p className="text-[10px] text-slate-500">Brand</p>
            </div>
            <div className="flex-1 text-center p-3 bg-slate-50 rounded-xl">
              <p className="text-2xl font-bold text-slate-500" style={{ fontFamily: "JetBrains Mono" }}>{data.brand_vs_distractor?.distractor_elements_pct || 0}%</p>
              <p className="text-[10px] text-slate-500">Distractors</p>
            </div>
          </div>
        </MetricCard>
        <MetricCard title="Assessment">
          <p className="text-sm text-slate-600 leading-relaxed">{data.position_assessment}</p>
          <p className="text-xs text-orange-600 mt-3 font-medium">{data.benchmark_text}</p>
        </MetricCard>
      </div>
    </div>
  );
}

/* ═══ NEURO SCORE TAB ═══ */
function NeuroScoreTab({ data, isTrial, onUpgrade }) {
  if (!data) return <EmptyTab />;
  const dims = ["attention", "cognitive_clarity", "emotional_connection", "brand_visibility", "memorability", "cta_effectiveness"];
  const labels = { attention: "Attention", cognitive_clarity: "Cognitive Clarity", emotional_connection: "Emotional Connection", brand_visibility: "Brand Visibility", memorability: "Memorability", cta_effectiveness: "CTA Effectiveness" };
  const values = dims.map(d => (data[d]?.score || 0) / 100);

  return (
    <div data-testid="neuro-tab" className="space-y-5">
      {/* Radar chart */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-base font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>6-Dimension Analysis</h3>
        <div className="flex justify-center">
          <svg viewBox="0 0 240 240" className="w-64 h-64">
            {[1, 0.66, 0.33].map((s, si) => (
              <polygon key={si} points={hexPoints(120, 120, 90 * s)} fill="none" stroke="#E2E8F0" strokeWidth="0.5" />
            ))}
            <polygon points={dataPoints(120, 120, 90, values)} fill="rgba(249,115,22,0.15)" stroke="#F97316" strokeWidth="2" />
            {dims.map((d, i) => {
              const a = (Math.PI * 2 * i) / 6 - Math.PI / 2;
              const x = 120 + Math.cos(a) * 108;
              const y = 120 + Math.sin(a) * 108;
              return <text key={d} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="7" fill="#94A3B8" fontFamily="JetBrains Mono, monospace">{labels[d]?.split(" ")[0]}</text>;
            })}
          </svg>
        </div>
      </div>

      {/* Dimension cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {dims.map(d => {
          const dim = data[d];
          if (!dim) return null;
          return (
            <div key={d} data-testid={`dimension-${d}`} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-slate-700">{labels[d]}</h4>
                <div className="flex items-center gap-2">
                  <span className={`text-xl font-bold ${dim.score >= 80 ? "text-emerald-500" : dim.score >= 50 ? "text-amber-500" : "text-red-500"}`} style={{ fontFamily: "JetBrains Mono" }}>{dim.score}</span>
                  <span className="text-[10px] text-slate-400">wt: {dim.weight}%</span>
                </div>
              </div>
              {isTrial ? (
                <div className="relative mt-2">
                  <div className="filter blur-sm select-none pointer-events-none">
                    <p className="text-xs text-slate-400 mb-2">What this measures and key insights</p>
                    <p className="text-sm text-slate-600 leading-relaxed">This detailed finding explains why your ad scored this way on this dimension and provides specific actionable insights.</p>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button onClick={onUpgrade} className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-xl px-3 py-1.5 flex items-center gap-1.5 shadow-sm hover:shadow transition-all">
                      <Lock size={12} className="text-orange-500" />
                      <span className="text-xs font-semibold text-slate-700">Upgrade to view</span>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-400 mb-2">{dim.measured}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{dim.finding}</p>
                  <p className="text-[10px] text-slate-400 mt-2">Category avg: <span className="font-bold" style={{ fontFamily: "JetBrains Mono" }}>{dim.category_avg}</span></p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ ENGAGEMENT TAB ═══ */
function EngagementTab({ data, platform }) {
  if (!data) return <EmptyTab />;
  return (
    <div data-testid="engagement-tab" className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
          <p className="text-xs text-slate-500 mb-2">Predicted Engagement</p>
          <Badge className={`text-lg px-4 py-1 ${data.level === "High" ? "bg-emerald-100 text-emerald-700" : data.level === "Medium" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"} border-0`}>
            {data.level}
          </Badge>
          <p className="text-xs text-slate-400 mt-2">{data.percentile}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
          <p className="text-xs text-slate-500 mb-2">Platform Fit ({platform})</p>
          <ScoreRing score={data.platform_fit_score || 0} size={80} />
          <p className="text-xs text-slate-500 mt-2">{data.platform_fit_notes}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <p className="text-xs text-slate-500 mb-3">Predicted Metrics</p>
          <MetricRow label="CTR Range" value={data.predicted_ctr || "—"} />
          <MetricRow label="Engagement Rate" value={data.predicted_engagement_rate || "—"} />
          <MetricRow label="View-Through" value={data.predicted_view_through || "—"} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <MetricCard title="Engagement Drivers">
          {(data.drivers || []).map((d, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <span className="text-sm text-slate-700 flex items-center gap-2"><span className="text-emerald-500">+</span>{d.factor}</span>
              <span className="text-xs font-bold text-emerald-600" style={{ fontFamily: "JetBrains Mono" }}>{d.impact}</span>
            </div>
          ))}
        </MetricCard>
        <MetricCard title="Engagement Risks">
          {(data.risks || []).map((r, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <span className="text-sm text-slate-700 flex items-center gap-2"><span className="text-red-500">-</span>{r.factor}</span>
              <span className="text-xs font-bold text-red-500" style={{ fontFamily: "JetBrains Mono" }}>{r.impact}</span>
            </div>
          ))}
        </MetricCard>
      </div>
      <p className="text-[10px] text-slate-400 text-center">Predictions based on analysis of ads with similar visual and structural profiles. Actual results depend on targeting, budget, and market conditions.</p>
    </div>
  );
}

/* ═══ MEMORY RETENTION TAB ═══ */
function MemoryRetentionTab({ data, memorability }) {
  const [frequency, setFrequency] = useState(1);
  const freqLabels = { 1: "1x/week", 2: "2x/week", 3: "3x/week", 5: "5x/week", 7: "Daily", 10: "10x/week" };
  const freqValues = [1, 2, 3, 5, 7, 10];

  if (!data) return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
      <Brain size={32} className="mx-auto text-slate-300 mb-3" />
      <p className="text-sm text-slate-500">Memory retention data not available. Re-analyze to generate.</p>
    </div>
  );

  const curve = data.curves?.[String(frequency)] || [];
  const baselineCurve = data.curves?.["1"] || [];
  const maxDay = 30;

  // SVG chart dimensions
  const W = 600, H = 280, PL = 50, PR = 20, PT = 20, PB = 40;
  const chartW = W - PL - PR, chartH = H - PT - PB;
  const x = (day) => PL + (day / maxDay) * chartW;
  const y = (ret) => PT + ((100 - ret) / 100) * chartH;

  const curvePath = curve.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.day).toFixed(1)},${y(p.retention).toFixed(1)}`).join(" ");
  const baselinePath = baselineCurve.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.day).toFixed(1)},${y(p.retention).toFixed(1)}`).join(" ");
  const areaPath = curvePath + ` L${x(30).toFixed(1)},${y(0).toFixed(1)} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z`;

  const day7 = curve.find(p => p.day === 7)?.retention || 0;
  const day30 = curve.find(p => p.day === 30)?.retention || 0;
  const strengthLabel = data.memory_strength >= 70 ? "Strong" : data.memory_strength >= 45 ? "Moderate" : "Weak";
  const strengthColor = data.memory_strength >= 70 ? "#10B981" : data.memory_strength >= 45 ? "#F59E0B" : "#EF4444";

  return (
    <div data-testid="memory-tab" className="space-y-5">
      {/* Memory strength summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center">
          <p className="text-xs text-slate-500 mb-1">Memory strength</p>
          <p className="text-3xl font-bold" style={{ fontFamily: "JetBrains Mono", color: strengthColor }}>{Math.round(data.memory_strength)}</p>
          <p className="text-xs mt-1" style={{ color: strengthColor }}>{strengthLabel}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center">
          <p className="text-xs text-slate-500 mb-1">Estimated recall at Day 7</p>
          <p className="text-3xl font-bold text-slate-800" style={{ fontFamily: "JetBrains Mono" }}>{day7}%</p>
          <p className="text-xs text-slate-400 mt-1">at {freqLabels[frequency]} frequency</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center">
          <p className="text-xs text-slate-500 mb-1">Estimated recall at Day 30</p>
          <p className="text-3xl font-bold text-slate-800" style={{ fontFamily: "JetBrains Mono" }}>{day30}%</p>
          <p className="text-xs text-slate-400 mt-1">at {freqLabels[frequency]} frequency</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Memory retention curve</h3>
            <p className="text-xs text-slate-400 mt-0.5">Based on Ebbinghaus forgetting function with spacing effect</p>
          </div>
        </div>

        {/* Frequency slider */}
        <div className="mb-5 bg-slate-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-slate-600">Ad frequency (exposures per week)</label>
            <span className="text-sm font-bold text-orange-600" style={{ fontFamily: "JetBrains Mono" }}>{freqLabels[frequency]}</span>
          </div>
          <input
            type="range" min="0" max="5" step="1"
            value={freqValues.indexOf(frequency)}
            onChange={(e) => setFrequency(freqValues[parseInt(e.target.value)])}
            className="w-full accent-orange-500"
          />
          <div className="flex justify-between mt-1">
            {freqValues.map(f => (
              <span key={f} className={`text-[9px] ${frequency === f ? "text-orange-600 font-bold" : "text-slate-400"}`}>{freqLabels[f]}</span>
            ))}
          </div>
        </div>

        {/* SVG Chart */}
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 300 }}>
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(v => (
            <g key={v}>
              <line x1={PL} y1={y(v)} x2={W - PR} y2={y(v)} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray={v === 0 ? "0" : "4,4"} />
              <text x={PL - 8} y={y(v) + 4} textAnchor="end" fontSize="10" fill="#94A3B8">{v}%</text>
            </g>
          ))}
          {/* X axis labels */}
          {[0, 1, 3, 7, 14, 30].map(d => (
            <text key={d} x={x(d)} y={H - 8} textAnchor="middle" fontSize="10" fill="#94A3B8">Day {d}</text>
          ))}

          {/* Baseline curve (1x/week) — show if frequency > 1 */}
          {frequency > 1 && (
            <path d={baselinePath} fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="6,4" />
          )}

          {/* Area fill */}
          <path d={areaPath} fill="url(#retGrad)" opacity="0.3" />

          {/* Main curve */}
          <path d={curvePath} fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Data points */}
          {curve.map((p, i) => (
            <g key={i}>
              <circle cx={x(p.day)} cy={y(p.retention)} r="4" fill="#FFF" stroke="#F97316" strokeWidth="2" />
              {p.day > 0 && (
                <text x={x(p.day)} y={y(p.retention) - 10} textAnchor="middle" fontSize="10" fontWeight="600" fill="#F97316">{p.retention}%</text>
              )}
            </g>
          ))}

          {/* Gradient def */}
          <defs>
            <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F97316" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 justify-center">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-0.5 bg-orange-500 rounded" />
            <span className="text-[10px] text-slate-500">{freqLabels[frequency]}</span>
          </div>
          {frequency > 1 && (
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-0.5 bg-slate-300 rounded" style={{ borderTop: "1.5px dashed #CBD5E1" }} />
              <span className="text-[10px] text-slate-400">1x/week (baseline)</span>
            </div>
          )}
        </div>
      </div>

      {/* Methodology note */}
      <div className="bg-slate-50 rounded-xl px-5 py-3">
        <p className="text-[11px] text-slate-400 leading-relaxed">
          Estimated retention based on the Ebbinghaus forgetting function (1885, replicated by Murre & Dros, PLOS ONE 2015), 
          with memory strength modulated by emotional encoding (Cahill & McGaugh 1998), visual distinctiveness (Von Restorff 1933), 
          cognitive load (Sweller 1988), and brand encoding (Keller 1993). Repetition effect modeled using spacing benefit coefficient 
          from Cepeda et al. 2006. These are model-based estimates, not guarantees of actual recall rates.
        </p>
      </div>
    </div>
  );
}

/* ═══ RECOMMENDATIONS TAB ═══ */
function RecommendationsTab({ data, isTrial, onUpgrade, analysisId, userPlan, originalImage, optimizedImage: existingOptimized }) {
  const [selected, setSelected] = useState([]);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizedImage, setOptimizedImage] = useState(existingOptimized || null);
  const [optimizeError, setOptimizeError] = useState("");

  if (!data || data.length === 0) return <EmptyTab />;

  const priorityColor = { "High Impact": "bg-red-50 text-red-700 border-red-200", "Medium Impact": "bg-amber-50 text-amber-700 border-amber-200", "Quick Win": "bg-emerald-50 text-emerald-700 border-emerald-200" };
  const effortColor = { Easy: "text-emerald-600", Moderate: "text-amber-600", "Redesign Required": "text-red-500" };
  const visibleCount = isTrial ? 2 : data.length;
  const canOptimize = ["growth", "agency", "enterprise"].includes(userPlan);
  const alreadyOptimized = !!existingOptimized;

  const toggleSelect = (idx) => {
    setSelected(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  const handleOptimize = async () => {
    if (selected.length === 0 || optimizing || alreadyOptimized) return;
    setOptimizing(true);
    setOptimizeError("");
    try {
      const { data: result } = await axios.post(`${API}/analyses/${analysisId}/optimize`, {
        analysis_id: analysisId,
        selected_recommendations: selected,
      }, { withCredentials: true });
      setOptimizedImage(result.optimized_image);
    } catch (err) {
      setOptimizeError(err.response?.data?.detail || "Optimization failed. Please try again.");
    }
    setOptimizing(false);
  };

  return (
    <div data-testid="recommendations-tab" className="space-y-4">
      {/* Optimize bar — Growth+ only */}
      {!isTrial && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>
                AI Image Optimization
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {canOptimize
                  ? alreadyOptimized
                    ? "This ad has been optimized. 1 optimization per analysis."
                    : `Select recommendations below, then click optimize. ${selected.length} selected.`
                  : "Available on Growth plan and above."}
              </p>
            </div>
            {canOptimize ? (
              <Button
                onClick={handleOptimize}
                disabled={selected.length === 0 || optimizing || alreadyOptimized}
                className="rounded-xl h-10 px-5 text-sm font-semibold text-white border-0 disabled:opacity-50"
                style={{ background: selected.length > 0 && !alreadyOptimized ? "linear-gradient(135deg, #F97316 0%, #D946EF 100%)" : "#E2E8F0" }}>
                {optimizing ? (
                  <span className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Optimizing...</span>
                ) : alreadyOptimized ? (
                  <span className="flex items-center gap-2"><Lock size={14} /> Already Optimized</span>
                ) : (
                  <span className="flex items-center gap-2"><Sparkles size={14} /> Optimize Ad ({selected.length})</span>
                )}
              </Button>
            ) : (
              <Button onClick={onUpgrade} className="gradient-btn rounded-xl h-9 px-5 text-xs font-semibold">
                Upgrade to Growth <ArrowRight size={12} className="ml-1" />
              </Button>
            )}
          </div>
          {optimizeError && (
            <p className="text-xs text-red-500 mt-2">{optimizeError}</p>
          )}
        </div>
      )}

      {/* Optimized result — before/after */}
      {optimizedImage && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-orange-500" />
            <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>AI Optimized Version</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 mb-2 font-medium">Original</p>
              <img src={originalImage} alt="Original ad" className="rounded-xl border border-slate-200 w-full object-contain max-h-[400px]" />
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-2 font-medium">Optimized</p>
              <img src={optimizedImage} alt="Optimized ad" className="rounded-xl border border-orange-200 w-full object-contain max-h-[400px]" />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <a href={optimizedImage} download="adbit-optimized-ad.png"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold text-white"
              style={{ background: "linear-gradient(135deg, #F97316 0%, #D946EF 100%)" }}>
              <Download size={14} /> Download Optimized Ad
            </a>
          </div>
          <p className="text-[10px] text-slate-400 mt-3">
            AI-generated concept. Review and adjust brand elements before publishing.
          </p>
        </div>
      )}

      {/* Recommendation cards with checkboxes */}
      {(data || []).slice(0, visibleCount).map((rec, i) => (
        <div
          key={i}
          data-testid={`recommendation-${i}`}
          className={`bg-white rounded-2xl border p-5 transition-all cursor-pointer ${
            selected.includes(i) ? "border-orange-400 ring-2 ring-orange-400/20" : "border-slate-200 hover:border-slate-300"
          } ${canOptimize && !alreadyOptimized ? "" : ""}`}
          onClick={() => { if (canOptimize && !alreadyOptimized && !isTrial) toggleSelect(i); }}
        >
          <div className="flex items-start gap-3">
            {/* Checkbox — only for Growth+ */}
            {canOptimize && !isTrial && (
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                selected.includes(i)
                  ? "bg-orange-500 border-orange-500"
                  : alreadyOptimized ? "border-slate-200 bg-slate-50" : "border-slate-300 hover:border-orange-400"
              }`}>
                {selected.includes(i) && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </div>
            )}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge className={`text-[10px] ${priorityColor[rec.priority] || priorityColor["Medium Impact"]} border`}>{rec.priority}</Badge>
                <Badge variant="outline" className="text-[10px]">{rec.principle}</Badge>
                <span className={`text-[10px] font-medium ml-auto ${effortColor[rec.effort] || ""}`}>{rec.effort}</span>
              </div>
              <h4 className="text-sm font-bold text-slate-800 mb-1">{rec.title}</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{rec.description}</p>
              <p className="text-xs font-bold text-orange-600 mt-2" style={{ fontFamily: "JetBrains Mono" }}>{rec.expected_impact}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Blurred recs for trial */}
      {isTrial && data.length > visibleCount && (
        <div className="relative">
          <div className="space-y-4 filter blur-sm select-none pointer-events-none">
            {data.slice(visibleCount, visibleCount + 2).map((rec, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Badge className={`text-[10px] ${priorityColor[rec.priority] || priorityColor["Medium Impact"]} border`}>{rec.priority}</Badge>
                </div>
                <h4 className="text-sm font-bold text-slate-800 mb-1">{rec.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{rec.description}</p>
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/50 backdrop-blur-[1px] rounded-2xl">
            <Lock size={20} className="text-orange-500 mb-2" />
            <p className="text-sm font-bold text-slate-800 mb-1">+{data.length - visibleCount} more recommendations</p>
            <p className="text-xs text-slate-500 mb-3">Upgrade to view all recommendations</p>
            <Button onClick={onUpgrade} className="gradient-btn rounded-xl h-9 px-5 text-xs font-semibold">
              Upgrade Plan <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══ AUDIENCE TAB ═══ */
function AudienceTab({ data }) {
  if (!data) return <EmptyTab />;
  return (
    <div data-testid="audience-tab" className="space-y-5">
      <Badge className="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 border text-xs">Beta</Badge>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {["primary", "secondary"].map(type => {
          const a = data[type];
          if (!a) return null;
          return (
            <div key={type} className="bg-white rounded-2xl border border-slate-200 p-5">
              <h4 className="text-sm font-bold text-slate-700 capitalize mb-3">{type} Audience</h4>
              <div className="space-y-2 text-sm">
                <AudienceRow label="Demographics" value={a.demographics} />
                <AudienceRow label="Psychographics" value={a.psychographics} />
                <AudienceRow label="Interests" value={a.interests} />
                <AudienceRow label="Best Platform" value={a.platform} />
              </div>
              <p className="text-xs text-slate-500 mt-3 italic">{a.reasoning}</p>
            </div>
          );
        })}
      </div>
      {data.fit_matrix && (
        <MetricCard title="Audience-Creative Fit Matrix">
          {data.fit_matrix.map((f, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <span className="text-sm text-slate-700">{f.segment}</span>
              <Badge className={`text-[10px] ${f.fit === "High" ? "bg-emerald-50 text-emerald-700" : f.fit === "Medium" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"} border-0`}>{f.fit}</Badge>
            </div>
          ))}
        </MetricCard>
      )}
      {data.adaptations && (
        <MetricCard title="Adaptation Suggestions">
          {data.adaptations.map((a, i) => (
            <div key={i} className="py-2 border-b border-slate-50 last:border-0">
              <p className="text-xs font-bold text-orange-600">{a.audience}</p>
              <p className="text-sm text-slate-600">{a.suggestion}</p>
            </div>
          ))}
        </MetricCard>
      )}
    </div>
  );
}

/* ═══ SHARED COMPONENTS ═══ */
function MetricCard({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <h4 className="text-sm font-bold text-slate-700 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h4>
      {children}
    </div>
  );
}

function MetricRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-xs font-medium text-slate-700" style={{ fontFamily: "JetBrains Mono" }}>{value}</span>
    </div>
  );
}

function LegendDot({ color, label }) {
  return <div className="flex items-center gap-1.5"><div className={`w-3 h-3 rounded-sm ${color}`} /><span className="text-xs text-slate-500">{label}</span></div>;
}

function AudienceRow({ label, value }) {
  return (
    <div className="flex gap-2"><span className="text-xs text-slate-400 w-24 flex-shrink-0">{label}</span><span className="text-xs text-slate-700">{value}</span></div>
  );
}

function ScoreRing({ score, size = 80 }) {
  const r = size * 0.38;
  const c = Math.PI * 2 * r;
  const color = score >= 80 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E2E8F0" strokeWidth="5" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="5" strokeDasharray={`${(score/100)*c} ${c}`} strokeLinecap="round" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-lg font-bold" style={{ fontFamily: "JetBrains Mono", color }}>{score}</span>
    </div>
  );
}

function EmptyTab() {
  return <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center"><p className="text-sm text-slate-400">No data available for this section.</p></div>;
}

function LoadingSkeleton() {
  return (
    <div data-testid="analysis-loading" className="space-y-5 max-w-6xl">
      <div className="h-6 w-32 bg-slate-200 rounded animate-pulse" />
      <div className="bg-white rounded-2xl border border-slate-200 p-6 h-48 animate-pulse" />
      <div className="bg-white rounded-2xl border border-slate-200 p-6 h-96 animate-pulse" />
    </div>
  );
}

function ProcessingState() {
  return (
    <div data-testid="analysis-processing" className="max-w-md mx-auto mt-20 text-center">
      <Loader2 size={32} className="text-orange-500 animate-spin mx-auto mb-4" />
      <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Analysis in Progress</h2>
      <p className="text-sm text-slate-500 mt-1">This usually takes a few minutes...</p>
    </div>
  );
}

function FailedState({ error, onBack }) {
  return (
    <div data-testid="analysis-failed" className="max-w-md mx-auto mt-20 text-center">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
        <span className="text-red-500 text-xl font-bold">!</span>
      </div>
      <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Analysis Failed</h2>
      <p className="text-sm text-slate-500 mt-1 mb-4">{error || "An error occurred during analysis."}</p>
      <Button onClick={onBack} variant="outline" className="rounded-xl">Back to Analyses</Button>
    </div>
  );
}

function hexPoints(cx, cy, r) {
  return Array.from({ length: 6 }, (_, i) => { const a = (Math.PI*2*i)/6 - Math.PI/2; return `${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`; }).join(" ");
}
function dataPoints(cx, cy, r, vals) {
  return vals.map((v, i) => { const a = (Math.PI*2*i)/6 - Math.PI/2; return `${cx+Math.cos(a)*r*v},${cy+Math.sin(a)*r*v}`; }).join(" ");
}
